import { ref, watch, computed, Ref } from '@vue/runtime-core';

/**
 * Type pour les clés de localStorage utilisées dans l'application
 */
export type StorageKey =
  | 'brand'
  | 'user_data'
  | 'auth_token'
  | 'user_logo'
  | 'user_signatures'
  | 'redirect_after_brand_change'
  | 'last_brand_change_time';

/**
 * Interface pour les données utilisateur
 */
export interface UserData {
  id: number;
  name: string;
  email: string;
  role?: string;
  [key: string]: any;
}

/**
 * Interface pour le logo utilisateur
 */
export interface UserLogo {
  url: string;
  [key: string]: any;
}

/**
 * Type mapping pour les valeurs du localStorage
 */
interface StorageValueMap {
  brand: string;
  user_data: UserData | null;
  auth_token: string | null;
  user_logo: UserLogo | null;
  user_signatures: any[] | null;
  redirect_after_brand_change: string | null;
  last_brand_change_time: string | null;
}

/**
 * Cache réactif pour les valeurs du localStorage
 */
const storageCache = new Map<StorageKey, Ref<any>>();

/**
 * Événement personnalisé pour la synchronisation cross-tab
 */
const STORAGE_EVENT = 'app-storage-change';

/**
 * Classe pour gérer les événements de storage personnalisés
 */
class StorageEvent extends Event {
  constructor(public key: StorageKey, public newValue: any, public oldValue: any) {
    super(STORAGE_EVENT);
  }
}

/**
 * Émetteur d'événements pour les changements de storage
 */
const storageEventTarget = new EventTarget();

/**
 * Parse une valeur JSON de manière sécurisée
 */
function safeParse<T>(value: string | null, defaultValue: T): T {
  if (!value) return defaultValue;
  try {
    return JSON.parse(value) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Obtient une valeur réactive du localStorage
 * La valeur est synchronisée automatiquement entre tous les composants et onglets
 */
export function useStorage<K extends StorageKey>(
  key: K,
  defaultValue: StorageValueMap[K]
): Ref<StorageValueMap[K]> {
  // Vérifier si on a déjà un ref pour cette clé
  if (storageCache.has(key)) {
    return storageCache.get(key) as Ref<StorageValueMap[K]>;
  }

  // Lire la valeur initiale
  const initialValue = getStorageValue(key, defaultValue);
  const storageRef = ref(initialValue) as Ref<StorageValueMap[K]>;

  // Sauvegarder dans le cache
  storageCache.set(key, storageRef);

  // Watcher pour synchroniser avec le localStorage
  watch(
    storageRef,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        setStorageValue(key, newValue);

        // Émettre un événement pour la synchronisation cross-tab
        storageEventTarget.dispatchEvent(
          new StorageEvent(key, newValue, oldValue)
        );
      }
    },
    { deep: true }
  );

  // Écouter les changements depuis d'autres onglets
  const handleStorageChange = (e: Event) => {
    if (e instanceof StorageEvent && e.key === key) {
      storageRef.value = e.newValue;
    }
  };

  // Écouter l'événement storage natif du navigateur
  const handleNativeStorageChange = (e: globalThis.StorageEvent) => {
    if (e.key === key && e.newValue !== null) {
      const newValue = safeParse(e.newValue, defaultValue);
      storageRef.value = newValue;
    }
  };

  storageEventTarget.addEventListener(STORAGE_EVENT, handleStorageChange);
  window.addEventListener('storage', handleNativeStorageChange);

  return storageRef;
}

/**
 * Obtient une valeur du localStorage
 */
export function getStorageValue<K extends StorageKey>(
  key: K,
  defaultValue: StorageValueMap[K]
): StorageValueMap[K] {
  try {
    const item = localStorage.getItem(key);
    if (!item) return defaultValue;

    // Pour les objets, parser le JSON
    if (key === 'user_data' || key === 'user_logo' || key === 'user_signatures') {
      return safeParse(item, defaultValue);
    }

    // Pour les strings simples
    return item as StorageValueMap[K];
  } catch (error) {
    console.error(`Erreur lors de la lecture de ${key}:`, error);
    return defaultValue;
  }
}

/**
 * Définit une valeur dans le localStorage
 */
export function setStorageValue<K extends StorageKey>(
  key: K,
  value: StorageValueMap[K]
): void {
  try {
    const oldValue = getStorageValue(key, value);

    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      const stringValue = typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);
      localStorage.setItem(key, stringValue);
    }

    // Émettre un événement pour notifier les autres composants
    storageEventTarget.dispatchEvent(
      new StorageEvent(key, value, oldValue)
    );

    // Dispatch un événement storage natif pour la synchronisation cross-tab
    window.dispatchEvent(
      new globalThis.StorageEvent('storage', {
        key,
        newValue: typeof value === 'object' ? JSON.stringify(value) : String(value),
        oldValue: typeof oldValue === 'object' ? JSON.stringify(oldValue) : String(oldValue),
        storageArea: localStorage,
        url: window.location.href,
      })
    );
  } catch (error) {
    console.error(`Erreur lors de l'écriture de ${key}:`, error);
  }
}

/**
 * Supprime une valeur du localStorage
 */
export function removeStorageValue(key: StorageKey): void {
  try {
    const oldValue = localStorage.getItem(key);
    localStorage.removeItem(key);

    // Émettre un événement
    storageEventTarget.dispatchEvent(
      new StorageEvent(key, null, oldValue)
    );
  } catch (error) {
    console.error(`Erreur lors de la suppression de ${key}:`, error);
  }
}

/**
 * Vide tout le localStorage
 */
export function clearStorage(): void {
  try {
    localStorage.clear();

    // Réinitialiser le cache
    storageCache.clear();

    // Émettre un événement global de nettoyage
    window.dispatchEvent(new Event('storage-cleared'));
  } catch (error) {
    console.error('Erreur lors du nettoyage du storage:', error);
  }
}

/**
 * Vérifie si une clé existe dans le localStorage
 */
export function hasStorageValue(key: StorageKey): boolean {
  return localStorage.getItem(key) !== null;
}

/**
 * Écoute les changements d'une clé spécifique
 */
export function watchStorage<K extends StorageKey>(
  key: K,
  callback: (newValue: StorageValueMap[K], oldValue: StorageValueMap[K]) => void
): () => void {
  const handler = (e: Event) => {
    if (e instanceof StorageEvent && e.key === key) {
      callback(e.newValue, e.oldValue);
    }
  };

  storageEventTarget.addEventListener(STORAGE_EVENT, handler);

  // Retourner une fonction de nettoyage
  return () => {
    storageEventTarget.removeEventListener(STORAGE_EVENT, handler);
  };
}

/**
 * Hook pour obtenir les informations de l'utilisateur connecté de manière réactive
 */
export function useCurrentUser() {
  const userData = useStorage('user_data', null);

  const isLoggedIn = computed(() => userData.value !== null);
  const userId = computed(() => userData.value?.id ?? null);
  const userName = computed(() => userData.value?.name ?? '');
  const userEmail = computed(() => userData.value?.email ?? '');

  const setUserData = (data: UserData | null) => {
    userData.value = data;
  };

  const logout = () => {
    userData.value = null;
    removeStorageValue('auth_token');
  };

  return {
    userData,
    isLoggedIn,
    userId,
    userName,
    userEmail,
    setUserData,
    logout,
  };
}

/**
 * Hook pour obtenir la marque courante de manière réactive
 */
export function useCurrentBrand() {
  const brand = useStorage('brand', '');

  const setBrand = (newBrand: string) => {
    brand.value = newBrand.toLowerCase();
  };

  const getBrandDisplayName = computed(() => {
    const brandMap: Record<string, string> = {
      hv: 'Horizon Vert',
      valtra: 'Valtra',
      fendt: 'Fendt',
      agco: 'AGCO',
    };
    return brandMap[brand.value] || brand.value.toUpperCase();
  });

  const getBrandLogo = computed(() => {
    const logoMap: Record<string, string> = {
      hv: '/images/logo-hv.svg',
      valtra: '/images/logo-valtra-black.svg',
      fendt: '/images/logo-fendt.svg',
      agco: '/images/logo-agco.svg',
    };
    return logoMap[brand.value] || null;
  });

  return {
    brand,
    setBrand,
    brandDisplayName: getBrandDisplayName,
    brandLogo: getBrandLogo,
  };
}

/**
 * Hook pour le token d'authentification
 */
export function useAuthToken() {
  const token = useStorage('auth_token', null);

  const isAuthenticated = computed(() => token.value !== null);

  const setToken = (newToken: string) => {
    token.value = newToken;
  };

  const clearToken = () => {
    token.value = null;
  };

  return {
    token,
    isAuthenticated,
    setToken,
    clearToken,
  };
}

/**
 * Hook pour le logo utilisateur
 */
export function useUserLogo() {
  const userLogo = useStorage('user_logo', null);

  const logoUrl = computed(() => userLogo.value?.url || null);

  const setUserLogo = (logo: UserLogo | null) => {
    userLogo.value = logo;
  };

  return {
    userLogo,
    logoUrl,
    setUserLogo,
  };
}
